<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps({
  editor: {
    type: Object,
    required: true,
  },
})

const BUTTON_GROUPS = [
  {
    id: 'headings',
    buttons: [
      {
        key: 'heading',
        extensionName: 'heading',
        isHeadingMenu: true,
        i18nKey: 'richTextEditor.toolbar.heading',
        command: null,
      },
    ],
  },
  {
    id: 'formatting',
    buttons: [
      {
        key: 'bold',
        extensionName: 'bold',
        icon: 'format_bold',
        i18nKey: 'richTextEditor.toolbar.bold',
        command: (editor) => editor.chain().focus().toggleBold().run(),
      },
      {
        key: 'italic',
        extensionName: 'italic',
        icon: 'format_italic',
        i18nKey: 'richTextEditor.toolbar.italic',
        command: (editor) => editor.chain().focus().toggleItalic().run(),
      },
      {
        key: 'strike',
        extensionName: 'strike',
        icon: 'format_strikethrough',
        i18nKey: 'richTextEditor.toolbar.strike',
        command: (editor) => editor.chain().focus().toggleStrike().run(),
      },
    ],
  },
  {
    id: 'lists',
    buttons: [
      {
        key: 'bulletList',
        extensionName: 'bulletList',
        icon: 'format_list_bulleted',
        i18nKey: 'richTextEditor.toolbar.bulletList',
        command: (editor) => editor.chain().focus().toggleBulletList().run(),
      },
      {
        key: 'orderedList',
        extensionName: 'orderedList',
        icon: 'format_list_numbered',
        i18nKey: 'richTextEditor.toolbar.orderedList',
        command: (editor) => editor.chain().focus().toggleOrderedList().run(),
      },
    ],
  },
  {
    id: 'link',
    buttons: [
      {
        key: 'link',
        extensionName: 'link',
        icon: 'link',
        i18nKey: 'richTextEditor.toolbar.link',
        isLink: true,
        command: null, // handled separately via the popover
      },
    ],
  },
  {
    id: 'image',
    buttons: [
      {
        key: 'image',
        extensionName: 'image',
        icon: 'image',
        i18nKey: 'richTextEditor.toolbar.image',
        isImage: true,
        command: null,
      },
    ],
  },
  {
    id: 'history',
    buttons: [
      {
        key: 'undo',
        extensionName: 'undoRedo',
        icon: 'undo',
        i18nKey: 'richTextEditor.toolbar.undo',
        command: (editor) => editor.chain().focus().undo().run(),
        canExecute: (editor) => editor.can().undo(),
      },
      {
        key: 'redo',
        extensionName: 'undoRedo',
        icon: 'redo',
        i18nKey: 'richTextEditor.toolbar.redo',
        command: (editor) => editor.chain().focus().redo().run(),
        canExecute: (editor) => editor.can().redo(),
      },
    ],
  },
]

const HEADING_LEVELS = [
  { level: 1, i18nKey: 'richTextEditor.toolbar.heading1', sizeClass: 'text-2xl font-bold' },
  { level: 2, i18nKey: 'richTextEditor.toolbar.heading2', sizeClass: 'text-xl font-bold' },
  { level: 3, i18nKey: 'richTextEditor.toolbar.heading3', sizeClass: 'text-lg font-semibold' },
  { level: 4, i18nKey: 'richTextEditor.toolbar.heading4', sizeClass: 'text-base font-semibold' },
]

// Filter groups by registered extensions
const visibleGroups = computed(() => {
  const extensionNames = props.editor.extensionManager.extensions.map((e) => e.name)
  return BUTTON_GROUPS.map((group) => ({
    ...group,
    buttons: group.buttons.filter((btn) => extensionNames.includes(btn.extensionName)),
  })).filter((group) => group.buttons.length > 0)
})

// Heading menu
const headingMenuRef = ref()
const headingMenuItems = computed(() =>
  HEADING_LEVELS.map(({ level, i18nKey, sizeClass }) => ({
    label: t(i18nKey),
    sizeClass,
    isActive: isEditorActive('heading', { level }),
    command: () => applyHeading(level),
  }))
)

function toggleHeadingMenu(event) {
  headingMenuRef.value.toggle(event)
}

function applyHeading(level) {
  props.editor.chain().focus().toggleHeading({ level }).run()
}

// Image popover state
const showImagePopover = ref(false)
const imageUrl = ref('')
const imagePopoverRef = ref(null)

function toggleImagePopover() {
  if (showImagePopover.value) {
    closeImagePopover()
    return
  }
  const existingSrc = props.editor.getAttributes('image').src ?? ''
  imageUrl.value = existingSrc
  showImagePopover.value = true
}

function closeImagePopover() {
  showImagePopover.value = false
  imageUrl.value = ''
}

function applyImage() {
  if (imageUrl.value.trim()) {
    props.editor.chain().focus().setImage({ src: imageUrl.value.trim() }).run()
  }
  closeImagePopover()
}

function removeImage() {
  props.editor.chain().focus().deleteSelection().run()
  closeImagePopover()
}

function onImageInputKeydown(event) {
  if (event.key === 'Escape') {
    // Keep the Esc inside the popover so a surrounding Dialog doesn't close too.
    event.stopPropagation()
    closeImagePopover()
  } else if (event.key === 'Enter') {
    event.preventDefault()
    applyImage()
  }
}

function onImageDocumentClick(event) {
  const el = Array.isArray(imagePopoverRef.value) ? imagePopoverRef.value[0] : imagePopoverRef.value
  if (el && !el.contains(event.target)) {
    closeImagePopover()
  }
}

watch(showImagePopover, (isVisible) => {
  if (isVisible) {
    setTimeout(() => {
      document.addEventListener('click', onImageDocumentClick)
    }, 0)
  } else {
    document.removeEventListener('click', onImageDocumentClick)
  }
})

// Link popover state
const showLinkPopover = ref(false)
const linkUrl = ref('')
const popoverRef = ref(null)
const linkInputRef = ref(null)

function toggleLinkPopover() {
  if (showLinkPopover.value) {
    closeLinkPopover()
    return
  }

  // Pre-fill with existing href if cursor is on a link
  const existingHref = props.editor.getAttributes('link').href ?? ''
  linkUrl.value = existingHref

  showLinkPopover.value = true
}

function closeLinkPopover() {
  showLinkPopover.value = false
  linkUrl.value = ''
}

function applyLink() {
  if (linkUrl.value.trim()) {
    props.editor.chain().focus().extendMarkRange('link').setLink({ href: linkUrl.value.trim() }).run()
  }
  closeLinkPopover()
}

function removeLink() {
  props.editor.chain().focus().unsetLink().run()
  closeLinkPopover()
}

function onLinkInputKeydown(event) {
  if (event.key === 'Escape') {
    // Keep the Esc inside the popover so a surrounding Dialog doesn't close too.
    event.stopPropagation()
    closeLinkPopover()
  } else if (event.key === 'Enter') {
    event.preventDefault()
    applyLink()
  }
}

// Click-outside handling for the link popover
function onDocumentClick(event) {
  const el = Array.isArray(popoverRef.value) ? popoverRef.value[0] : popoverRef.value
  if (el && !el.contains(event.target)) {
    closeLinkPopover()
  }
}

// Attach / detach the document listener whenever popover visibility changes.
watch(showLinkPopover, (isVisible) => {
  if (isVisible) {
    // Defer so the click that opened the popover doesn't immediately close it.
    setTimeout(() => {
      document.addEventListener('click', onDocumentClick)
    }, 0)
  } else {
    document.removeEventListener('click', onDocumentClick)
  }
})

// Reactive counter bumped on every editor transaction so template re-evaluates
const updatedAt = ref(Date.now())

function onTransaction() {
  updatedAt.value = Date.now()
}

watch(
  () => props.editor,
  (editor, oldEditor) => {
    oldEditor?.off('transaction', onTransaction)
    editor?.on('transaction', onTransaction)
  },
  { immediate: true }
)

function isEditorActive(name, attrs) {
  updatedAt.value // reactive dependency — DO NOT REMOVE
  return props.editor?.isActive(name, attrs) ?? false
}

function isButtonDisabled(btn) {
  updatedAt.value // reactive dependency — DO NOT REMOVE
  if (!btn.canExecute) return false
  return !btn.canExecute(props.editor)
}

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick)
  document.removeEventListener('click', onImageDocumentClick)
  props.editor?.off('transaction', onTransaction)
})
</script>

<template>
  <div class="flex flex-wrap items-center gap-0.5 p-1 bg-gray-700 border-b border-gray-600 rounded-t-lg">
    <template v-for="(group, groupIndex) in visibleGroups" :key="group.id">
      <!-- Divider between groups (not before the first, not after the last) -->
      <div
        v-if="groupIndex > 0"
        class="w-px self-stretch bg-gray-500 mx-0.5"
      />

      <!-- Buttons in the group -->
      <template v-for="btn in group.buttons" :key="btn.key">
        <!-- Special case: heading dropdown menu -->
        <div v-if="btn.isHeadingMenu">
          <div class="rounded" :class="{ 'bg-gray-600': isEditorActive('heading') }">
            <Button
              :id="'btn-rte-' + btn.key"
              type="button"
              text
              size="small"
              v-tooltip.bottom="$t(btn.i18nKey)"
              @mousedown.prevent
              @click="toggleHeadingMenu"
            >
              <span class="flex items-center">
                <span class="text-[16px] font-bold leading-none">{{ $t('richTextEditor.toolbar.headingShortLabel') }}</span>
                <span class="material-icons" style="font-size: 16px; line-height: 1">arrow_drop_down</span>
              </span>
            </Button>
          </div>
        </div>

        <!-- Special case: image button with inline popover -->
        <div v-if="btn.isImage" class="relative">
          <div class="rounded" :class="{ 'bg-gray-600': isEditorActive('image') }">
            <Button
              :id="'btn-rte-' + btn.key"
              type="button"
              text
              size="small"
              v-tooltip.bottom="$t(btn.i18nKey)"
              @mousedown.prevent
              @click="toggleImagePopover"
            >
              <span class="material-icons">{{ btn.icon }}</span>
            </Button>
          </div>

          <div
            v-if="showImagePopover"
            ref="imagePopoverRef"
            class="absolute top-full left-1/2 -translate-x-1/2 mt-1 z-20 flex flex-col gap-2 bg-gray-800 border border-gray-600 rounded-lg p-2 shadow-lg min-w-72"
          >
            <input
              id="input-rte-image-url"
              v-model="imageUrl"
              type="url"
              :placeholder="$t('richTextEditor.toolbar.imagePlaceholder')"
              class="w-full px-3 py-1 bg-gray-900 border border-gray-600 rounded text-sm text-white focus:outline-none focus:ring-1 focus:ring-cyan-500"
              @keydown="onImageInputKeydown"
            />
            <!-- Not on an image: Insert -->
            <div v-if="!isEditorActive('image')" class="flex gap-2">
              <Button
                id="btn-rte-insert-image"
                type="button"
                text
                size="small"
                :label="$t('richTextEditor.toolbar.imageInsert')"
                @mousedown.prevent
                @click="applyImage"
              />
            </div>
            <!-- On an image: Update + Remove -->
            <div v-else class="flex gap-2">
              <Button
                id="btn-rte-update-image"
                type="button"
                text
                size="small"
                :label="$t('richTextEditor.toolbar.imageUpdate')"
                @mousedown.prevent
                @click="applyImage"
              />
              <Button
                id="btn-rte-remove-image"
                type="button"
                text
                size="small"
                :label="$t('richTextEditor.toolbar.imageRemove')"
                @mousedown.prevent
                @click="removeImage"
              />
            </div>
          </div>
        </div>

        <!-- Special case: link button with inline popover -->
        <div v-if="btn.isLink" class="relative">
          <div class="rounded" :class="{ 'bg-gray-600': isEditorActive('link') }">
            <Button
              :id="'btn-rte-' + btn.key"
              type="button"
              text
              size="small"
              v-tooltip.bottom="$t(btn.i18nKey)"
              @mousedown.prevent
              @click="toggleLinkPopover"
            >
              <span class="material-icons">{{ btn.icon }}</span>
            </Button>
          </div>

          <!-- Inline link popover -->
          <div
            v-if="showLinkPopover"
            ref="popoverRef"
            class="absolute top-full left-1/2 -translate-x-1/2 mt-1 z-20 flex flex-col gap-2 bg-gray-800 border border-gray-600 rounded-lg p-2 shadow-lg min-w-72"
          >
            <input
              id="input-rte-link-url"
              ref="linkInputRef"
              v-model="linkUrl"
              type="url"
              :placeholder="$t('richTextEditor.toolbar.linkPlaceholder')"
              class="w-full px-3 py-1 bg-gray-900 border border-gray-600 rounded text-sm text-white focus:outline-none focus:ring-1 focus:ring-cyan-500"
              @keydown="onLinkInputKeydown"
            />
            <!-- Cursor is NOT on a link: Apply -->
            <div v-if="!isEditorActive('link')" class="flex gap-2">
              <Button
                id="btn-rte-apply-link"
                type="button"
                text
                size="small"
                :label="$t('richTextEditor.toolbar.linkApply')"
                @mousedown.prevent
                @click="applyLink"
              />
            </div>
            <!-- Cursor IS on a link: Update + Remove -->
            <div v-else class="flex gap-2">
              <Button
                id="btn-rte-update-link"
                type="button"
                text
                size="small"
                :label="$t('richTextEditor.toolbar.linkUpdate')"
                @mousedown.prevent
                @click="applyLink"
              />
              <Button
                id="btn-rte-remove-link"
                type="button"
                text
                size="small"
                :label="$t('richTextEditor.toolbar.linkRemove')"
                @mousedown.prevent
                @click="removeLink"
              />
            </div>
          </div>
        </div>

        <!-- Standard button -->
        <div v-if="!btn.isHeadingMenu && !btn.isImage && !btn.isLink" class="rounded" :class="{ 'bg-gray-600': isEditorActive(btn.extensionName, btn.activeAttrs) }">
          <Button
            :id="'btn-rte-' + btn.key"
            type="button"
            text
            size="small"
            v-tooltip.bottom="$t(btn.i18nKey)"
            :disabled="isButtonDisabled(btn)"
            @mousedown.prevent
            @click="btn.command(editor)"
          >
            <span v-if="btn.icon" class="material-icons">{{ btn.icon }}</span>
            <span v-else class="text-sm font-bold leading-none">{{ btn.text }}</span>
          </Button>
        </div>
      </template>
    </template>
  </div>
  <!-- The popup Menu is appended to <body>: without stopping Esc here, PrimeVue Dialog's
       document-level listener would close a host dialog (e.g. the Q&A editor) together with the menu. -->
  <Menu ref="headingMenuRef" :model="headingMenuItems" popup @keydown.esc.stop>
    <template #item="{ item, props }">
      <a v-bind="props.action" data-telemetry-id="rte-heading-menu-item" class="flex items-center justify-between gap-4">
        <span :style="{ color: 'var(--p-primary-color)' }" :class="item.sizeClass">{{ item.label }}</span>
        <span v-if="item.isActive" class="material-icons" style="font-size: 18px; color: var(--p-primary-color)">check</span>
      </a>
    </template>
  </Menu>
</template>

<style scoped>
:deep(.p-button:disabled) {
  cursor: not-allowed !important;
}

span.material-icons {
  font-size: 20px;
}
</style>
